/**
 * createFeedbackTicket — REST call for POST /admin/feedback/ticket, the
 * human-triggered Tickety ticket creation action on the admin feedback
 * page (specs/open/feedback-submissions-management.md §3/§4/§5).
 *
 * Unlike fetchJson's callers, this has several distinguishable failure
 * modes the caller must be able to react to differently (409 already-
 * ticketed vs. 502 Tickety rejection vs. everything else) -- unwrapped
 * fetchJson (throw-status-in-message-only) would force the UI to parse
 * error message text to tell these apart, so this call is implemented
 * directly rather than reusing fetchJson. Mirrors fetchJson's own
 * no-token-throws contract instead of duplicating a different one.
 */
import { authService } from './auth';
import { API_URL } from './common/constants';

export interface CreateFeedbackTicketResult {
  ticketId: string;
  ticketUrl: string;
}

/**
 * Thrown for any non-2xx response. `status` lets the caller distinguish
 * 409 (already ticketed -- existingTicketId is populated from the
 * response body) and 502 (Tickety rejected the call) from other errors
 * without parsing `message` text.
 */
export class CreateFeedbackTicketError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly existingTicketId?: string,
  ) {
    super(message);
    this.name = 'CreateFeedbackTicketError';
  }
}

/**
 * Creates a Tickety ticket for a single feedback item, identified by
 * turnId. Throws CreateFeedbackTicketError on any non-2xx response, or a
 * plain Error if there's no valid auth token.
 */
export async function createFeedbackTicket(turnId: string, adminNotes?: string): Promise<CreateFeedbackTicketResult> {
  const token = authService.getIdToken();
  if (!token) throw new Error('Unable to create feedback ticket: no valid auth token');

  const res = await fetch(`${API_URL}/admin/feedback/ticket`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(adminNotes !== undefined ? { turnId, adminNotes } : { turnId }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new CreateFeedbackTicketError(
      res.status,
      typeof data.error === 'string' ? data.error : `Unable to create feedback ticket: server responded ${res.status}`,
      typeof data.ticketId === 'string' ? data.ticketId : undefined,
    );
  }
  return data as CreateFeedbackTicketResult;
}
